/**
 * equity.ts —— 赢率引擎（PRD 模块 4，本产品核心，口径不可改）
 *
 * ============================ 口径（PRD 4.1 / 4.3） ============================
 *  · 输入 = 我的 2 张底牌 + 已公开公共牌（0/3/4/5 张）。**不接受对手人数参数**。
 *  · 对每个可能的完整 5 张公共牌面，枚举剩余 45 张未知牌的全部 C(45,2)=990 个
 *    两张组合，与我的 7 张牌力比较，得 |L|（对手输我）/|T|（平）/|W|（对手赢我）。
 *  · win = |L|/990、tie = |T|/990、lose = |W|/990、equity = (|L| + 0.5|T|)/990。
 *  · 对所有未来完整牌面**等权平均**。
 *  · 零采样、零近似：无蒙特卡洛、无 `1-(1-p)^N` 独立性假设、无剪枝、无插值。
 *  · 引擎内**不含任何随机源**，同输入逐位相同（AC4.4）。
 *
 * ============================ 为什么未来公共牌不能省 ============================
 *  「谁比我大」只有 5 张公共牌全部确定后才有定论。若只在当前已开牌面上比大小，
 *  得到的是「当前牌力分位」而非赢率（翻牌听牌会被严重低估）。所以翻牌/转牌
 *  必须枚举全部未来公共牌组合（1,081 / 46 种），这部分不可省略（PRD 4.3）。
 *
 * ============================ 性能实现手段 ============================
 *  1. 全程 Int32Array / 原始 number，主循环内**零对象分配**（避免 GC 抖动）；
 *  2. 我的牌力按牌面复用（每个完整牌面只算一次，而非 990 次）；
 *  3. 未知牌预先展开成 45 个「(位图槽位, 位)」对，内循环只做一次 OR；
 *  4. 外层固定 3 个花色位图、内层只改动变化的那个，减少寄存器搬运；
 *  5. 比较结果直接累加（`l += (v < mine) ? 1 : 0`），避免分支误预测。
 */

import { NUM_CARDS, assertDistinctCards, handClassLabel, type Card } from './cards';
import { evaluateMasks } from './evaluator';
import { lookupPreflop, isPreflopTableAvailable, preflopTableStatus } from './preflopTable';

/** 街。数值即已公开公共牌张数对应的阶段，便于日志排序。 */
export type Street = 'preflop' | 'flop' | 'turn' | 'river';

/** C(45,2) —— 单个完整牌面下对手两张牌的组合总数。全街恒定，与对手人数无关。 */
export const OPPONENT_COMBOS = 990;

/** 赢率结果。所有比例为 [0,1] 区间的精确有理数的浮点表示。 */
export interface EquityResult {
  /** 我击败一名随机对手的概率。 */
  readonly win: number;
  /** 与一名随机对手打平的概率。 */
  readonly tie: number;
  /** 被一名随机对手击败的概率。 */
  readonly lose: number;
  /** 底池份额期望 = win + 0.5 * tie。 */
  readonly equity: number;
  /** 本次计算所处的街。 */
  readonly street: Street;
  /** 实际枚举的完整牌面数（翻牌前查表时为 0）。 */
  readonly boardsEnumerated: number;
  /** 完整牌面总数（等于 boardsEnumerated，除非被取消）。 */
  readonly boardsTotal: number;
  /** 是否命中翻牌前离线表（埋点 calc_result.hit_table）。 */
  readonly fromTable: boolean;
  /** 恒为 true —— 本引擎无近似路径。埋点 calc_result.exact 直接取此字段。 */
  readonly exact: true;
  /** Worker 收到请求到返回结果的耗时（ms），供 calc_result / calc_slow 埋点。 */
  readonly elapsedMs: number;
  /**
   * 精确计数原始值，供 CI 自洽校验（AC4.2③）：
   * w + t + l 恒等于 990 × boardsTotal。翻牌前查表时为离线表的整数计数。
   *
   * 口径统一为**英雄视角**，且翻牌前 / 翻牌后两条路径完全一致：
   *   w = 我赢的对手组合数、t = 打平数、l = 我输的组合数。
   * 注意离线表原始资产（preflop_counts.json）里的字段 `L` 是「对手更弱」即 hero-win，
   * 语义与此相反，转换时已在 preflopTable.ts 内归一，切勿直接混用。
   */
  readonly counts: { readonly l: number; readonly t: number; readonly w: number; readonly denominator: number };
}

/** 进度回调（PRD 4.6：每 50ms 上报一次真实完成度，禁止假进度）。 */
export interface EquityProgress {
  readonly street: Street;
  readonly boardsEnumerated: number;
  readonly boardsTotal: number;
  /** [0,1]，= boardsEnumerated / boardsTotal，来自真实枚举进度。 */
  readonly ratio: number;
  readonly elapsedMs: number;
}

/** 取消原因（PRD 4.6 四个触发点 + 埋点 calc_cancel.reason）。 */
export type CancelReason = 'calculator_closed' | 'new_community_card' | 'hand_ended' | 'player_acted' | 'superseded';

/** 取消令牌。UI 侧持有并在四个触发点调用 cancel()。 */
export interface CancelToken {
  readonly cancelled: boolean;
  readonly reason: CancelReason | null;
  cancel(reason: CancelReason): void;
}

export function createCancelToken(): CancelToken {
  let cancelled = false;
  let reason: CancelReason | null = null;
  return {
    get cancelled() { return cancelled; },
    get reason() { return reason; },
    cancel(r: CancelReason) {
      if (!cancelled) { cancelled = true; reason = r; }
    },
  };
}

/** 计算被取消时抛出的错误。调用方据此丢弃结果，禁止写入 UI（AC4.7）。 */
export class EquityCancelledError extends Error {
  readonly reason: CancelReason;
  constructor(reason: CancelReason) {
    super(`赢率计算已取消：${reason}`);
    this.name = 'EquityCancelledError';
    this.reason = reason;
  }
}

/** 翻牌前离线表不可用时抛出。UI 应展示「暂不可用」而非任何数值（PRD 4.5 / AC4.5）。 */
export class PreflopTableUnavailableError extends Error {
  /** 表不可用的具体原因（中文，来自 preflopTableStatus().reason）。 */
  readonly detail: string;
  constructor(detail: string) {
    super(`翻牌前离线表不可用：${detail}`);
    this.name = 'PreflopTableUnavailableError';
    this.detail = detail;
  }
}

export interface EquityOptions {
  /** 进度回调。按 progressIntervalMs 节流。 */
  readonly onProgress?: (p: EquityProgress) => void;
  /** 进度上报间隔，默认 50ms（PRD 4.6）。 */
  readonly progressIntervalMs?: number;
  /** 取消令牌。 */
  readonly cancelToken?: CancelToken;
  /**
   * 取消检查间隔（每处理多少个牌面检查一次）。默认 32。
   * 翻牌圈 1081 个牌面、单牌面约 30~60µs ⇒ 最坏中断延迟 ≈ 2ms，远优于 AC4.7 的 100ms。
   */
  readonly cancelCheckInterval?: number;
}

const now = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();

/** 各街的完整牌面枚举数量（PRD 4.2 表）。 */
export function boardsToEnumerate(street: Street): number {
  switch (street) {
    case 'preflop': return 2118760; // C(50,5)，走离线表不在运行时枚举
    case 'flop': return 1081;       // C(47,2)
    case 'turn': return 46;
    case 'river': return 1;
  }
}

export function streetOfBoard(boardLength: number): Street {
  switch (boardLength) {
    case 0: return 'preflop';
    case 3: return 'flop';
    case 4: return 'turn';
    case 5: return 'river';
    default:
      throw new Error(`公共牌张数必须为 0/3/4/5，收到 ${boardLength} 张`);
  }
}

/**
 * 计算精确赢率。
 *
 * @param hole  我的 2 张底牌
 * @param board 已公开公共牌，长度必须是 0 / 3 / 4 / 5
 *
 * 注意：**没有对手人数参数**，这是 PRD 4.1 的硬口径。
 */
export function calculateEquity(
  hole: readonly Card[],
  board: readonly Card[],
  options: EquityOptions = {},
): EquityResult {
  const t0 = now();
  if (hole.length !== 2) throw new Error(`底牌必须是 2 张，收到 ${hole.length} 张`);
  const street = streetOfBoard(board.length);
  assertDistinctCards([...hole, ...board], '赢率输入');

  // 取消检查必须覆盖**所有**路径，包括常数时间的翻牌前查表（AC4.7）：
  // 调用方在四个触发点取消后，绝不允许再拿到任何可写入 UI 的数值。
  if (options.cancelToken?.cancelled) {
    throw new EquityCancelledError(options.cancelToken.reason ?? 'superseded');
  }

  if (street === 'preflop') {
    // 翻牌前走离线表，不做运行时枚举（PRD 4.2 / AC4.5）
    if (!isPreflopTableAvailable()) {
      throw new PreflopTableUnavailableError(preflopTableStatus().reason ?? 'unknown');
    }
    const label = handClassLabel(hole[0], hole[1]);
    const entry = lookupPreflop(label);
    return {
      win: entry.win,
      tie: entry.tie,
      lose: entry.lose,
      equity: entry.equity,
      street,
      boardsEnumerated: 0,
      boardsTotal: boardsToEnumerate('preflop'),
      fromTable: true,
      exact: true,
      elapsedMs: now() - t0,
      counts: { l: entry.l, t: entry.t, w: entry.w, denominator: entry.denominator },
    };
  }

  return enumerateExact(hole, board, street, t0, options);
}

/**
 * 翻牌 / 转牌 / 河牌的实时精确计数。
 * 这是全引擎最热的代码路径，改动前务必先跑 bench.test.ts。
 */
function enumerateExact(
  hole: readonly Card[],
  board: readonly Card[],
  street: Street,
  t0: number,
  options: EquityOptions,
): EquityResult {
  const { onProgress, cancelToken } = options;
  const progressInterval = options.progressIntervalMs ?? 50;
  const cancelCheckInterval = options.cancelCheckInterval ?? 32;

  // ---- 已知牌位图与「未开牌池」 ----
  const used = new Uint8Array(NUM_CARDS);
  for (const c of hole) used[c] = 1;
  for (const c of board) used[c] = 1;

  // 未开牌 = 52 - 2 底牌 - 已开公共牌
  const unseen: number[] = [];
  for (let c = 0; c < NUM_CARDS; c++) if (used[c] === 0) unseen.push(c);
  const unseenCount = unseen.length; // flop:47, turn:46, river:45
  const futureCards = 5 - board.length;

  // 预展开每张未开牌的「花色槽位 + 点数位」，内循环避免重复 >>> / & 运算
  const slotOf = new Int32Array(unseenCount);
  const bitOf = new Int32Array(unseenCount);
  for (let i = 0; i < unseenCount; i++) {
    slotOf[i] = unseen[i] & 3;
    bitOf[i] = 1 << (unseen[i] >>> 2);
  }

  // 已开公共牌的 4 个花色位图
  const b0 = new Int32Array(4);
  for (const c of board) b0[c & 3] |= 1 << (c >>> 2);
  // 我的底牌位图（与公共牌合并后即我的 7 张）
  const h0 = new Int32Array(4);
  for (const c of hole) h0[c & 3] |= 1 << (c >>> 2);

  const boardsTotal = boardsToEnumerate(street);

  // 累计计数用 number（最大值 990 × 1081 = 1,070,190，远小于 2^53，无精度风险）
  // 命名统一为**英雄视角**：totalWin = 我赢的对手组合数。切勿沿用「对手更弱记 L」的旧口径。
  let totalWin = 0;
  let totalTie = 0;
  let totalLose = 0;
  let boardsDone = 0;
  let lastProgress = t0;

  // 复用的临时缓冲：整个计算过程只分配一次
  const full = new Int32Array(4);       // 完整牌面的 4 个花色位图
  const mineMask = new Int32Array(4);   // 我的 7 张位图
  const oppSlot = new Int32Array(45);   // 当前牌面下 45 张未知牌的槽位
  const oppBit = new Int32Array(45);    // 当前牌面下 45 张未知牌的点数位
  const chosen = new Int32Array(2);     // 当前选中的未来公共牌在 unseen 中的下标

  /** 处理一个完整牌面：算我的 rank，再对 990 个对手组合计数。 */
  const processBoard = (usedFuture: number): void => {
    // 完整牌面位图 = 已开公共牌 + 本次选出的未来公共牌
    full[0] = b0[0]; full[1] = b0[1]; full[2] = b0[2]; full[3] = b0[3];
    for (let i = 0; i < usedFuture; i++) {
      full[slotOf[chosen[i]]] |= bitOf[chosen[i]];
    }

    // 我的 7 张牌力：每个完整牌面只算一次（而非 990 次）
    mineMask[0] = full[0] | h0[0];
    mineMask[1] = full[1] | h0[1];
    mineMask[2] = full[2] | h0[2];
    mineMask[3] = full[3] | h0[3];
    const mine = evaluateMasks(mineMask[0], mineMask[1], mineMask[2], mineMask[3]);

    // 剩余 45 张未知牌 = 未开牌池 去掉 本牌面用掉的未来公共牌
    let n = 0;
    for (let i = 0; i < unseenCount; i++) {
      let skip = false;
      for (let k = 0; k < usedFuture; k++) if (chosen[k] === i) { skip = true; break; }
      if (skip) continue;
      oppSlot[n] = slotOf[i];
      oppBit[n] = bitOf[i];
      n++;
    }

    // 990 个对手两张组合
    const f0 = full[0]; const f1 = full[1]; const f2 = full[2]; const f3 = full[3];
    let w = 0;
    let t = 0;
    for (let x = 0; x < n; x++) {
      // 外层先把第一张牌 OR 进去，内层只处理第二张
      let a = f0; let b = f1; let c = f2; let d = f3;
      switch (oppSlot[x]) {
        case 0: a |= oppBit[x]; break;
        case 1: b |= oppBit[x]; break;
        case 2: c |= oppBit[x]; break;
        default: d |= oppBit[x]; break;
      }
      for (let y = x + 1; y < n; y++) {
        const bit = oppBit[y];
        let v: number;
        switch (oppSlot[y]) {
          case 0: v = evaluateMasks(a | bit, b, c, d); break;
          case 1: v = evaluateMasks(a, b | bit, c, d); break;
          case 2: v = evaluateMasks(a, b, c | bit, d); break;
          default: v = evaluateMasks(a, b, c, d | bit); break;
        }
        // 对手 rank 更小 => 他输我 => 我赢，计入 win
        if (v < mine) w++;
        else if (v === mine) t++;
      }
    }

    totalWin += w;
    totalTie += t;
    totalLose += OPPONENT_COMBOS - w - t;
    boardsDone++;
  };

  /** 枚举未来公共牌的组合（depth 层递归，flop 时 depth=2）。 */
  const enumerateFuture = (start: number, depth: number): void => {
    if (depth === futureCards) {
      processBoard(futureCards);
      // 取消与进度检查放在牌面粒度：既保证 ≤100ms 中断响应，又不污染内层热循环
      if ((boardsDone & (cancelCheckInterval - 1)) === 0) {
        if (cancelToken?.cancelled) throw new EquityCancelledError(cancelToken.reason ?? 'superseded');
        if (onProgress) {
          const t = now();
          if (t - lastProgress >= progressInterval) {
            lastProgress = t;
            onProgress({
              street,
              boardsEnumerated: boardsDone,
              boardsTotal,
              ratio: boardsDone / boardsTotal,
              elapsedMs: t - t0,
            });
          }
        }
      }
      return;
    }
    for (let i = start; i < unseenCount; i++) {
      chosen[depth] = i;
      enumerateFuture(i + 1, depth + 1);
    }
  };

  enumerateFuture(0, 0);

  if (cancelToken?.cancelled) throw new EquityCancelledError(cancelToken.reason ?? 'superseded');

  // 自洽断言（AC4.2③）：|L|+|T|+|W| 必须精确等于 990 × 牌面数
  const denominator = OPPONENT_COMBOS * boardsDone;
  if (totalWin + totalTie + totalLose !== denominator) {
    throw new Error(
      `赢率计数自洽校验失败：|W|+|T|+|L| = ${totalWin + totalTie + totalLose}，期望 ${denominator}`,
    );
  }
  if (boardsDone !== boardsTotal) {
    throw new Error(`枚举牌面数 ${boardsDone} ≠ 预期 ${boardsTotal}，枚举逻辑有误`);
  }

  if (onProgress) {
    const t = now();
    onProgress({ street, boardsEnumerated: boardsDone, boardsTotal, ratio: 1, elapsedMs: t - t0 });
  }

  return {
    win: totalWin / denominator,
    tie: totalTie / denominator,
    lose: totalLose / denominator,
    equity: (totalWin + 0.5 * totalTie) / denominator,
    street,
    boardsEnumerated: boardsDone,
    boardsTotal,
    fromTable: false,
    exact: true,
    elapsedMs: now() - t0,
    counts: { l: totalLose, t: totalTie, w: totalWin, denominator },
  };
}

/**
 * 单个完整牌面的精确计数（PRD 4.3）。
 * 独立导出用于 CI 自洽校验与「范围命中分布」（模块 5 P1）。
 * 返回值同为**英雄视角**：w = 我赢的对手组合数，l = 我输的组合数，w+t+l === 990。
 * @param board 必须是 5 张完整公共牌
 */
export function countAgainstFullBoard(
  hole: readonly Card[],
  board: readonly Card[],
): { l: number; t: number; w: number; mine: number } {
  if (hole.length !== 2) throw new Error(`底牌必须是 2 张，收到 ${hole.length} 张`);
  if (board.length !== 5) throw new Error(`countAgainstFullBoard 需要 5 张完整公共牌，收到 ${board.length} 张`);
  assertDistinctCards([...hole, ...board], '单牌面计数输入');

  const used = new Uint8Array(NUM_CARDS);
  for (const c of hole) used[c] = 1;
  for (const c of board) used[c] = 1;

  const f = [0, 0, 0, 0];
  for (const c of board) f[c & 3] |= 1 << (c >>> 2);
  const mineMask = [f[0], f[1], f[2], f[3]];
  for (const c of hole) mineMask[c & 3] |= 1 << (c >>> 2);
  const mine = evaluateMasks(mineMask[0], mineMask[1], mineMask[2], mineMask[3]);

  const rest: number[] = [];
  for (let c = 0; c < NUM_CARDS; c++) if (used[c] === 0) rest.push(c);

  let w = 0;
  let t = 0;
  for (let x = 0; x < rest.length; x++) {
    for (let y = x + 1; y < rest.length; y++) {
      const m = [f[0], f[1], f[2], f[3]];
      m[rest[x] & 3] |= 1 << (rest[x] >>> 2);
      m[rest[y] & 3] |= 1 << (rest[y] >>> 2);
      const v = evaluateMasks(m[0], m[1], m[2], m[3]);
      if (v < mine) w++;
      else if (v === mine) t++;
    }
  }
  return { w, t, l: OPPONENT_COMBOS - w - t, mine };
}

/**
 * 已知双方底牌的对局赢率（AC4.2① 的引擎单测基准，如 AA vs KK）。
 * 这是**引擎自检工具**，不是产品口径 —— 产品侧永远是 vs 1 random。
 */
export function headsUpEquity(
  heroHole: readonly Card[],
  villainHole: readonly Card[],
  board: readonly Card[] = [],
): { win: number; tie: number; lose: number; equity: number; boards: number } {
  assertDistinctCards([...heroHole, ...villainHole, ...board], 'headsUpEquity 输入');
  const used = new Uint8Array(NUM_CARDS);
  for (const c of [...heroHole, ...villainHole, ...board]) used[c] = 1;
  const pool: number[] = [];
  for (let c = 0; c < NUM_CARDS; c++) if (used[c] === 0) pool.push(c);

  const need = 5 - board.length;
  const base = [0, 0, 0, 0];
  for (const c of board) base[c & 3] |= 1 << (c >>> 2);
  const hMask = [0, 0, 0, 0];
  for (const c of heroHole) hMask[c & 3] |= 1 << (c >>> 2);
  const vMask = [0, 0, 0, 0];
  for (const c of villainHole) vMask[c & 3] |= 1 << (c >>> 2);

  let win = 0;
  let tie = 0;
  let lose = 0;
  let boards = 0;
  const pickIdx = new Int32Array(need);

  const walk = (start: number, depth: number): void => {
    if (depth === need) {
      const f = [base[0], base[1], base[2], base[3]];
      for (let i = 0; i < need; i++) {
        const c = pool[pickIdx[i]];
        f[c & 3] |= 1 << (c >>> 2);
      }
      const hv = evaluateMasks(f[0] | hMask[0], f[1] | hMask[1], f[2] | hMask[2], f[3] | hMask[3]);
      const vv = evaluateMasks(f[0] | vMask[0], f[1] | vMask[1], f[2] | vMask[2], f[3] | vMask[3]);
      if (hv > vv) win++;
      else if (hv === vv) tie++;
      else lose++;
      boards++;
      return;
    }
    for (let i = start; i < pool.length; i++) {
      pickIdx[depth] = i;
      walk(i + 1, depth + 1);
    }
  };
  walk(0, 0);

  return { win: win / boards, tie: tie / boards, lose: lose / boards, equity: (win + 0.5 * tie) / boards, boards };
}
